/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-empty-function */
import { format } from "util";
import EmployeesChart from "components/charts/modern/EmployeesChart";
import FreeFloatChart from "components/charts/modern/FreeFloatChart";
import IndicatorVsQuoteChart from "components/charts/modern/IndicatorVsQuoteChart";
import MarketCapitalizationChart from "components/charts/modern/MarketCapitalizationChart";
import QuotesChart from "components/charts/modern/QuotesChart";
import SharesChart from "components/charts/modern/SharesChart";
import SharesholdersChart from "components/charts/modern/SharesholdersChart";
import TradesChart from "components/charts/modern/TradesChart";
import TrailingReturnsChart from "components/charts/modern/TrailingReturnsChart";
import CollapsibleContentSection from "components/company/CollapsibleContentSection";
import { SectionJumpIdentifiers } from "components/company/SectionJumpIdentifiers";
import StaticContentSection from "components/company/StaticContentSection";
import ContentContainer from "components/company/sections/ContentContainer";
import { ContentSection } from "components/company/sections/ContentSection";
import ContentSectionBreadcrumbs from "components/company/sections/ContentSectionBreadcrumbs";
import ContentSectionDescriptionLine from "components/company/sections/ContentSectionDescriptionLine";
import ContentSectionHeading from "components/company/sections/ContentSectionHeading";
import BrazilianIssuerFinancialHighlightsCollapsible from "components/company/sections/collapsible/BrazilianIssuerFinancialHighlightsCollapsible";
import EquityInterestsList from "components/company/sections/content/EquityInterestsList";
import FamilyRelationships from "components/company/sections/content/FamilyRelationships";
import IntangibleAssetsList from "components/company/sections/content/IntangibleAssetsList";
import Ipes from "components/company/sections/content/Ipes";
import KeyCustomersSection from "components/company/sections/content/KeyCustomersSection";
import Officers from "components/company/sections/content/Officers";
import PropertyPlantEquipmentList from "components/company/sections/content/PropertyPlantEquipmentList";
import TotalCompensations from "components/company/sections/content/TotalCompensations";
import ContentSectionLabeledCounters from "components/company/sections/summary/ContentSectionLabeledCounters";
import EquityInterestsSummary from "components/company/sections/summary/EquityInterestsSummary";
import MostRecentRelevantIpeSummary from "components/company/sections/summary/MostRecentRelevantIpeSummary";
import PropertyPlantEquipmentSummary from "components/company/sections/summary/PropertyPlantEquipmentSummary";
import TotalCompensationsSummary from "components/company/sections/summary/TotalCompensationsSummary";
import hasOneCompensation from "components/company/sections/summary/hasOneCompensation";
import BrazilianCompanySubsidiaries from "components/company/v2/BrazilianCompanySubsidiaries";
import CompanyIssuerGovernance from "components/company/v2/CompanyIssuerGovernance";
import CompanyIssuerHeaderCard from "components/company/v2/CompanyIssuerHeaderCard";
import CompanyIssuerRatings from "components/company/v2/CompanyIssuerRatings";
import ImpostoDeRenda from "components/company/v2/ImpostoDeRenda";
import YouTubeVideos from "components/company/v2/YouTubeVideos";
import EmployeesCountCard from "components/company/v2/cards/EmployeesCountCard";
import FoundedYearCard from "components/company/v2/cards/FoundedYearCard";
import HeadquartersCard from "components/company/v2/cards/HeadquartersCard";
import IndexParticipationCard from "components/company/v2/cards/IndexParticipationCard";
import MarketCapCard from "components/company/v2/cards/MarketCapCard";
import NextUpcomingCalendarEventCard from "components/company/v2/cards/NextUpcomingCalendarEventCard";
import ExchangeRegistrationDateCard from "components/company/v2/cards/RegistrationDateOrIPOCard";
import AssetDiscussion from "components/discussions/AssetDiscussion";
import Financials from "components/financials/modern/Financials";
import LocalFinancialsState from "components/financials/modern/LocalFinancialsState";
import Rating from "components/rating/Rating";
import FundamenteiMetadata from "components/seo/FundamenteiMetadata";
import { useSpecificSetting } from "components/settings/hooks/useSpecificSetting";
import TwoPassInViewRender from "components/shared/TwoPassInViewRender";
import OperatingSegments from "components/stocks/modern/OperatingSegments/OperatingSegments";
import OperatingSegmentsDataProvider from "components/stocks/modern/OperatingSegments/OperatingSegmentsDataProvider";
import { ScrollJumpButton, SectionBeacon, SpanPixelBeacon, StickyNavbarJumpers } from "components/ui/ScrollJump";
import classNames from "components/ui/classNames";
import conditionalCounters from "components/utils/conditionalCounters";
import { Provider } from "jotai";
import { useAmplitude } from "lib/amplitude/Amplitude";
import type { IBrazilianCompanyIssuerTopLevelFragment } from "lib/fragments/__generated__/BrazilianCompanyIssuerTopLevel.generated";
import useSelfie from "lib/hooks/useSelfie";
import { palette } from "lib/miolo";
import TickerSymbol from "lib/securities/TickerSymbol";
import getBaseUrl from "lib/utils/getBaseUrl";
import notUndefinedOrNull from "lib/utils/notUndefinedOrNull";
import sum from "lodash/fp/sum";
import Head from "next/head";
import Link from "next/link";
import { useCallback, useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { FormattedMessage, useIntl } from "react-intl";
import type { Updater } from "react-powerplug";
import { Value } from "react-powerplug";
import { jsonLdScriptProps } from "react-schemaorg";
import type { Corporation, Person } from "schema-dts";
import { IAssetType, IIndicatorDataSource, ISecurityCountry } from "types/graphqlTypes";
import BrazilianDividendsCollapsible from "../sections/collapsible/BrazilianIssuerDividendsCollapsible";
import CorporateActionsCollapsible from "../sections/collapsible/CorporateActionsCollapsible";

interface IBrazilianCompanyPageProps {
  issuer: IBrazilianCompanyIssuerTopLevelFragment;
  tickerSymbol: string;
  security: IBrazilianCompanyIssuerTopLevelFragment["securities"][0];
}

export default function BrazilianCompanyPage({ issuer, tickerSymbol, security }: IBrazilianCompanyPageProps) {
  const user = useSelfie();
  const amplitude = useAmplitude();
  const [activeContentSections, setActiveContentSections] = useState<ContentSection[]>([]);

  const toggleActiveContentSection = useCallback(
    (nextContentSection: ContentSection) => {
      return () => {
        if (activeContentSections.includes(nextContentSection)) {
          amplitude.logEvent("Closed Brazilian Company content section", {
            contentSection: nextContentSection,
          });
          setActiveContentSections(
            activeContentSections.filter((candidate) => {
              return candidate !== nextContentSection;
            }),
          );
        } else {
          amplitude.logEvent("Opened Brazilian Company content section", {
            contentSection: nextContentSection,
          });
          setActiveContentSections(activeContentSections.concat(nextContentSection));
        }
      };
    },
    [activeContentSections],
  );

  const isPremium = !!user?.isPremium;
  const isContentSectionActive = useCallback(
    (contentSection: ContentSection) => activeContentSections.includes(contentSection),
    [activeContentSections],
  );

  useEffect(() => {
    amplitude.logEvent("Viewed issuer", {
      category: "Ações",
      issuerId: issuer._id,
      tickerSymbol: tickerSymbol.toUpperCase(),
      name: issuer.name,
      sector: issuer.sector,
    });
  }, [issuer._id]);

  const tickerSymbolsWithQuotes = issuer.securities
    .filter((security) => security.hasQuotes)
    .map((security) => security.tickerSymbol);

  const availableTickerSymbols = tickerSymbolsWithQuotes.filter((tickerSymbol) => {
    return !TickerSymbol.isUnitStockSymbol(tickerSymbol);
  });

  const hasAvailableTickerSymbols = availableTickerSymbols.length > 0;

  return (
    <div>
      <BrazilianCompanyPageHead issuer={issuer} tickerSymbol={tickerSymbol} security={security} />

      <SpanPixelBeacon id={SectionJumpIdentifiers.Cover} />
      <div className="space-y-8">
        <div className="mx-auto max-w-7xl space-y-8 px-4 pb-8 xl:px-0">
          <div
            className={classNames(
              "relative mt-12 space-y-8 rounded-xl bg-white px-4 pb-8 pt-8 shadow",
              "border border-slate-200 ring-4 ring-slate-100 ring-offset-1 ring-offset-slate-100",
            )}
          >
            <div className="px-4">
              <CompanyIssuerHeaderCard issuer={issuer} security={security} tickerSymbol={tickerSymbol} />
            </div>

            <div className="grid grid-cols-1 gap-8 xl:grid-cols-2">
              <div className="space-y-7">
                <div className="w-full px-4">
                  <h6 className="font-aeonik text-sm font-bold uppercase tracking-wide text-gray-600">
                    Avaliações de {security.tickerSymbol}
                  </h6>

                  <div className="mt-2">
                    <Rating
                      issuer={issuer}
                      rankingPlacement={issuer.overallRankingPlacement}
                      rankingRatingSummary={issuer.overallRankingRatingSummary}
                    />
                  </div>
                </div>

                <CompanyIssuerGovernance issuer={issuer} />
              </div>

              <div className="space-y-8">
                {issuer.bookkeeper && <ImpostoDeRenda issuer={issuer} />}

                {issuer.reclameAquiRatings !== null && <CompanyIssuerRatings issuer={issuer} />}
              </div>
            </div>

            {issuer.subsidiaries.length > 0 && (
              <div className="px-4">
                <BrazilianCompanySubsidiaries subsidiaries={issuer.subsidiaries.slice(0, 4)} />
              </div>
            )}
          </div>

          <StickyNavbarJumpers>
            <ScrollJumpButton id={SectionJumpIdentifiers.Cover}>
              <FormattedMessage defaultMessage="Início" id="m42V4+" />
            </ScrollJumpButton>

            <ScrollJumpButton id={SectionJumpIdentifiers.Liquidity}>
              <FormattedMessage defaultMessage="Cotação" id="zV6SED" />
            </ScrollJumpButton>

            <ScrollJumpButton id={SectionJumpIdentifiers.Revenues}>
              <FormattedMessage defaultMessage="Receitas" id="DB2cb9" />
            </ScrollJumpButton>

            <ScrollJumpButton id={SectionJumpIdentifiers.Financials}>
              <FormattedMessage defaultMessage="Balanços" id="wauBuH" />
            </ScrollJumpButton>

            <ScrollJumpButton id={SectionJumpIdentifiers.Discussions}>
              <FormattedMessage defaultMessage="Discussões" id="aDdimF" />
            </ScrollJumpButton>
          </StickyNavbarJumpers>

          <div className="grid grid-cols-1 items-center gap-3 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            <MarketCapCard issuer={issuer} />
            <FoundedYearCard issuer={issuer} />
            <ExchangeRegistrationDateCard issuer={issuer} />
            <IndexParticipationCard issuer={issuer} />
            <HeadquartersCard issuer={issuer} />
            <EmployeesCountCard issuer={issuer} />
            {issuer.nextUpcomingCalendarEvent && <NextUpcomingCalendarEventCard issuer={issuer} />}
          </div>

          {hasAvailableTickerSymbols && (
            <div className="w-full">
              <StaticContentSection primaryColor={palette.oceanGreen} className="items-center gap-y-3">
                <div className="col-span-7 sm:col-span-6 md:col-span-5 lg:col-span-4">
                  <ContentSectionHeading>Valuation</ContentSectionHeading>
                  <ContentSectionBreadcrumbs segments={["Informações Financeiras", "Indicadores Financeiros"]} />
                </div>

                <div className="col-span-1" />

                <div className="col-span-12 sm:col-span-5 md:col-span-6 lg:col-span-7">
                  <div className="flex space-x-4">
                    {issuer.securities
                      .filter((security) => {
                        return !TickerSymbol.isUnitStockSymbol(security.tickerSymbol);
                      })
                      .map((security) => {
                        const linkHref = `/br/${security.tickerSymbol.toLowerCase()}/valuation`;
                        const linkAs = `/[country]/[tickerSymbol]/valuation`;

                        return (
                          <Link
                            key={`valuation/${security.tickerSymbol}`}
                            as={linkHref}
                            href={linkAs}
                            className={classNames(
                              "flex rounded-full bg-emerald-50 px-4 py-2 text-sm font-bold text-emerald-800",
                              "border border-transparent",
                              "hover:border-emerald-600",
                            )}
                          >
                            {security.tickerSymbol}
                          </Link>
                        );
                      })}
                  </div>
                </div>
              </StaticContentSection>
            </div>
          )}

          <div>
            <CollapsibleContentSection
              title="Comunicados"
              isExpanded={isContentSectionActive(ContentSection.COMUNICADOS)}
              onToggleCollapsed={toggleActiveContentSection(ContentSection.COMUNICADOS)}
              primaryColor={palette.viridian}
              renderSubtitle={() => (
                <ContentSectionDescriptionLine description="Últimos comunicados publicados na CVM" />
              )}
              renderContentSummary={() => {
                if (!issuer.mostRecentRelevantMaterialFact) {
                  return null;
                }

                return (
                  <MostRecentRelevantIpeSummary
                    mostRecentRelevantMaterialFact={issuer.mostRecentRelevantMaterialFact}
                  />
                );
              }}
              renderContent={() => (
                <ContentContainer>
                  <Ipes issuer={issuer} />
                </ContentContainer>
              )}
            />
          </div>

          <div className="space-y-9">
            <CollapsibleContentSection
              title="Administração"
              isExpanded={isContentSectionActive(ContentSection.ADMINISTRACAO)}
              isPremiumSection={true}
              onToggleCollapsed={toggleActiveContentSection(ContentSection.ADMINISTRACAO)}
              primaryColor={palette.mariner}
              renderSubtitle={() => (
                <ContentSectionBreadcrumbs segments={["Governança", "Assembléia e Administração"]} />
              )}
              renderContentSummary={() => {
                const officersCounts = conditionalCounters(issuer.officers, {
                  boardOfOfficers: ({ board }) => board === "Pertence apenas ao Conselho de Administração",
                  boardOfDirectors: ({ board }) => board === "Pertence apenas à Diretoria",
                  fiscalCouncil: ({ board }) => board === "Conselho Fiscal",
                });

                return (
                  <ContentSectionLabeledCounters
                    counters={[
                      { label: "Diretoria", totalCount: officersCounts.boardOfDirectors },
                      { label: "Administração", totalCount: officersCounts.boardOfOfficers },
                      { label: "Fiscal", totalCount: officersCounts.fiscalCouncil },
                    ]}
                  />
                );
              }}
              renderContent={() => (
                <ContentContainer>
                  <Officers issuer={issuer} />
                </ContentContainer>
              )}
            />

            {issuer.familyRelationships.length > 0 && (
              <CollapsibleContentSection
                title="Relações Familiares"
                isExpanded={isContentSectionActive(ContentSection.RELACOES_FAMILIARES)}
                isPremiumSection={true}
                onToggleCollapsed={toggleActiveContentSection(ContentSection.RELACOES_FAMILIARES)}
                primaryColor={palette.mariner}
                renderSubtitle={() => (
                  <ContentSectionBreadcrumbs segments={["Governança", "Assembléia e Administração"]} />
                )}
                renderContent={() => (
                  <ContentContainer>
                    <FamilyRelationships issuer={issuer} />
                  </ContentContainer>
                )}
              />
            )}

            {issuer.financials.yearly.length > 0 &&
              hasOneCompensation(issuer.totalCompensations, issuer.financials) && (
                <CollapsibleContentSection
                  title="Remuneração dos Administradores"
                  isExpanded={isContentSectionActive(ContentSection.REMUNERACAO_DOS_ADMINISTRADORES)}
                  isPremiumSection={true}
                  onToggleCollapsed={toggleActiveContentSection(ContentSection.REMUNERACAO_DOS_ADMINISTRADORES)}
                  primaryColor={palette.mariner}
                  renderSubtitle={() => (
                    <ContentSectionBreadcrumbs segments={["Governança", "Assembléia e Administração"]} />
                  )}
                  renderContentSummary={() => (
                    <TotalCompensationsSummary
                      totalCompensations={issuer.totalCompensations}
                      financials={issuer.financials}
                    />
                  )}
                  renderContent={() => (
                    <div className="space-y-4">
                      <ContentContainer className="overflow-scroll bg-white p-0">
                        <TotalCompensations
                          totalCompensations={issuer.totalCompensations}
                          financials={issuer.financials}
                        />
                      </ContentContainer>
                      <div className="flex justify-center font-aeonik text-sm font-normal tracking-wide">
                        <p className="rounded-xl border border-slate-200 bg-slate-100 px-4 py-2 text-slate-600">
                          Os valores de remuneração acima estão em <strong>Reais</strong>. {"Lucro e Receita em "}
                          <strong>milhões</strong>.
                        </p>
                      </div>
                    </div>
                  )}
                />
              )}

            {issuer.propertyPlantEquipment.length > 0 && (
              <CollapsibleContentSection
                title="Ativos Imobilizados"
                isExpanded={isContentSectionActive(ContentSection.ATIVOS_IMOBILIZADOS)}
                isPremiumSection={true}
                onToggleCollapsed={toggleActiveContentSection(ContentSection.ATIVOS_IMOBILIZADOS)}
                primaryColor={palette.mariner}
                renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Ativos Relevantes"]} />}
                renderContentSummary={() => (
                  <PropertyPlantEquipmentSummary propertyPlantEquipment={issuer.propertyPlantEquipment} />
                )}
                renderContent={() => (
                  <ContentContainer>
                    <PropertyPlantEquipmentList propertyPlantEquipment={issuer.propertyPlantEquipment} />
                  </ContentContainer>
                )}
              />
            )}

            {issuer.intangibleAssets.length > 0 && (
              <div>
                <CollapsibleContentSection
                  title="Ativos Intangíveis"
                  isExpanded={isContentSectionActive(ContentSection.ATIVOS_INTANGIVEIS)}
                  isPremiumSection={true}
                  onToggleCollapsed={toggleActiveContentSection(ContentSection.ATIVOS_INTANGIVEIS)}
                  primaryColor={palette.mariner}
                  renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Ativos Relevantes"]} />}
                  renderContentSummary={() => {
                    const types = [...new Set(issuer.intangibleAssets.map(({ type }) => type))];

                    let counters = types
                      .map((type) => {
                        const totalCount = issuer.intangibleAssets.filter(
                          (candidate) => candidate.type === type,
                        ).length;
                        return {
                          label: type,
                          totalCount,
                        };
                      })
                      .sort((a, b) => {
                        return b.totalCount - a.totalCount;
                      })
                      .map(({ label, totalCount }) => {
                        return {
                          // "Domínios" is a shorter display name for "Nome de domínio na internet"
                          label: label === "Nome de domínio na internet" ? "Domínios" : label,
                          totalCount,
                        };
                      });

                    if (counters.length > 3) {
                      // If there's more than 3 trackers, sum everything else into "Outros"
                      counters = [
                        ...counters.slice(0, 3),
                        {
                          label: "Outros",
                          totalCount: sum(counters.slice(3).map(({ totalCount }) => totalCount)),
                        },
                      ];
                    }

                    return <ContentSectionLabeledCounters counters={counters} />;
                  }}
                  renderContent={() => (
                    <ContentContainer>
                      <IntangibleAssetsList intangibleAssets={issuer.intangibleAssets} />
                    </ContentContainer>
                  )}
                />
              </div>
            )}

            {issuer.equityInterests.length > 0 && (
              <div>
                <CollapsibleContentSection
                  title="Participações em Sociedades"
                  isExpanded={isContentSectionActive(ContentSection.PARTICIPACOES_EM_SOCIEDADES)}
                  isPremiumSection={true}
                  onToggleCollapsed={toggleActiveContentSection(ContentSection.PARTICIPACOES_EM_SOCIEDADES)}
                  primaryColor={palette.mariner}
                  renderSubtitle={() => <ContentSectionBreadcrumbs segments={["Ativos Relevantes"]} />}
                  renderContentSummary={() => <EquityInterestsSummary equityInterests={issuer.equityInterests} />}
                  renderContent={() => (
                    <ContentContainer>
                      <EquityInterestsList equityInterests={issuer.equityInterests} />
                    </ContentContainer>
                  )}
                />
              </div>
            )}
          </div>

          {isPremium && issuer.keyCustomers && (
            <div className="w-full">
              <KeyCustomersSection keyCustomers={issuer.keyCustomers} />
            </div>
          )}

          <div className="w-full">
            <CorporateActionsCollapsible
              tickerSymbols={issuer.securities.map((node) => node.tickerSymbol)}
              isExpanded={isContentSectionActive(ContentSection.CORPORATE_ACTIONS)}
              onToggleCollapsed={toggleActiveContentSection(ContentSection.CORPORATE_ACTIONS)}
            />
          </div>

          {user && (
            <div className="w-full">
              <BrazilianDividendsCollapsible
                tickerSymbol={tickerSymbol}
                issuer={issuer}
                isExpanded={isContentSectionActive(ContentSection.DIVIDENDOS)}
                onToggleCollapsed={toggleActiveContentSection(ContentSection.DIVIDENDOS)}
              />
            </div>
          )}

          <TwoPassInViewRender>
            <div>
              <BrazilianIssuerFinancialHighlightsCollapsible
                issuer={issuer}
                isPremium={isPremium}
                isExpanded={isContentSectionActive(ContentSection.DESTAQUES_FINANCEIROS)}
                onToggleCollapsed={toggleActiveContentSection(ContentSection.DESTAQUES_FINANCEIROS)}
              />
            </div>
          </TwoPassInViewRender>

          <TrailingReturnsChart
            trailingReturns={issuer.securities.map((security) => security.trailingReturns).filter(notUndefinedOrNull)}
          />

          <TwoPassInViewRender tag="QuotesChart">
            <Value<string[]>
              key={`QuotesCharts/${tickerSymbolsWithQuotes.join("")}`}
              initial={tickerSymbolsWithQuotes.filter((candidate) => candidate === tickerSymbol)}
            >
              {({ value, set }) => (
                <SectionBeacon id={SectionJumpIdentifiers.Liquidity}>
                  <Provider>
                    <QuotesChart
                      tickerSymbols={value}
                      availableTickerSymbols={tickerSymbolsWithQuotes}
                      onToggleTickerSymbols={(tickerSymbols) => {
                        onToggleTickerSymbols(tickerSymbol, tickerSymbols, set);
                      }}
                    />
                  </Provider>
                </SectionBeacon>
              )}
            </Value>
          </TwoPassInViewRender>

          <TwoPassInViewRender tag="IndicatorVsQuoteChart">
            <Value<string[]>
              key={`IndicatorVsQuoteChart/${tickerSymbolsWithQuotes.join("")}`}
              initial={tickerSymbolsWithQuotes.filter((candidate) => candidate === tickerSymbol)}
            >
              {({ value, set }) => (
                <Provider>
                  <IndicatorVsQuoteChart
                    country={ISecurityCountry.Br}
                    tickerSymbols={value}
                    availableTickerSymbols={tickerSymbolsWithQuotes}
                    onToggleTickerSymbols={(tickerSymbols) => {
                      onToggleTickerSymbols(tickerSymbol, tickerSymbols, set);
                    }}
                  />
                </Provider>
              )}
            </Value>
          </TwoPassInViewRender>

          {hasAvailableTickerSymbols && (
            <TwoPassInViewRender tag="MarketCapitalizationChart">
              <Value<string[]>
                key={`MarketCapitalizationChart/${availableTickerSymbols.join("")}`}
                initial={[availableTickerSymbols[0]]}
              >
                {({ value, set }) => (
                  <Provider>
                    <MarketCapitalizationChart
                      tickerSymbols={value}
                      availableTickerSymbols={availableTickerSymbols}
                      onToggleTickerSymbols={(tickerSymbols) =>
                        onToggleTickerSymbols(availableTickerSymbols[0], tickerSymbols, set)
                      }
                    />
                  </Provider>
                )}
              </Value>
            </TwoPassInViewRender>
          )}

          <TwoPassInViewRender tag="TradingSessionsChart">
            <Value<string[]>
              key={`TradingSessionsChart/${tickerSymbolsWithQuotes.join("")}`}
              initial={tickerSymbolsWithQuotes.filter((candidate) => candidate === tickerSymbol)}
            >
              {({ value, set }) => (
                <TradesChart
                  tickerSymbols={value}
                  availableTickerSymbols={tickerSymbolsWithQuotes}
                  onToggleTickerSymbols={(tickerSymbols) => {
                    onToggleTickerSymbols(tickerSymbol, tickerSymbols, set);
                  }}
                />
              )}
            </Value>
          </TwoPassInViewRender>

          {hasAvailableTickerSymbols && (
            <TwoPassInViewRender tag="FreeFloatChart">
              <Value<string[]>
                key={`FreeFloatChart/${availableTickerSymbols.join("")}`}
                initial={[availableTickerSymbols[0]]}
              >
                {({ value, set }) => (
                  <FreeFloatChart
                    tickerSymbols={value}
                    availableTickerSymbols={availableTickerSymbols}
                    onToggleTickerSymbols={(tickerSymbols) => {
                      onToggleTickerSymbols(availableTickerSymbols[0], tickerSymbols, set);
                    }}
                  />
                )}
              </Value>
            </TwoPassInViewRender>
          )}

          <TwoPassInViewRender tag="EmployeesChart">
            <EmployeesChart tickerSymbols={[tickerSymbol]} />
          </TwoPassInViewRender>

          {hasAvailableTickerSymbols && (
            <TwoPassInViewRender tag="SharesChart">
              <Value<string[]>
                key={`SharesChart/${availableTickerSymbols.join("")}`}
                initial={[availableTickerSymbols[0]]}
              >
                {({ value, set }) => (
                  <SharesChart
                    tickerSymbols={value}
                    availableTickerSymbols={availableTickerSymbols}
                    onToggleTickerSymbols={(tickerSymbols) => {
                      onToggleTickerSymbols(availableTickerSymbols[0], tickerSymbols, set);
                    }}
                  />
                )}
              </Value>
            </TwoPassInViewRender>
          )}

          <TwoPassInViewRender tag="SharesholdersChart">
            <SharesholdersChart tickerSymbols={[tickerSymbol]} />
          </TwoPassInViewRender>

          <TwoPassInViewRender tag="OperatingSegments">
            <OperatingSegmentsDataProvider tickerSymbols={[tickerSymbol]}>
              {({ businessSegments, geographicSegments, loading }) => {
                if (businessSegments && geographicSegments) {
                  return (
                    <div className="relative space-y-8">
                      {!loading && <SpanPixelBeacon offset={-160} id={SectionJumpIdentifiers.Revenues} />}

                      <ErrorBoundary fallbackRender={() => null}>
                        <Provider>
                          <OperatingSegments
                            kind="BUSINESS"
                            tickerSymbols={[tickerSymbol]}
                            financialSegments={businessSegments}
                          />
                        </Provider>
                      </ErrorBoundary>
                      <ErrorBoundary fallbackRender={() => null}>
                        <Provider>
                          <OperatingSegments
                            kind="GEOGRAPHIC"
                            tickerSymbols={[tickerSymbol]}
                            financialSegments={geographicSegments}
                          />
                        </Provider>
                      </ErrorBoundary>
                    </div>
                  );
                }
                return null;
              }}
            </OperatingSegmentsDataProvider>
          </TwoPassInViewRender>
        </div>

        <FinancialsSection tickerSymbol={tickerSymbol} />

        <div className="mx-auto max-w-7xl space-y-8 px-4 pb-16 xl:px-0">
          {issuer.youtubeVideos.length > 0 && (
            <TwoPassInViewRender tag="YouTubeVideos">
              <YouTubeVideos
                key={issuer._id}
                youtubeVideos={isPremium ? issuer.youtubeVideos : issuer.youtubeVideos.slice(0, 1)}
              />
            </TwoPassInViewRender>
          )}

          <TwoPassInViewRender tag="AssetDiscussion">
            <AssetDiscussion assetType={IAssetType.BrazilianCompany} assetId={issuer._id} />
          </TwoPassInViewRender>
        </div>
      </div>
    </div>
  );
}

interface FinancialsSectionProps {
  tickerSymbol: string;
}
function FinancialsSection({ tickerSymbol }: FinancialsSectionProps) {
  const { value: fullBleed } = useSpecificSetting("financialsTable.fullBleed");

  return (
    <div
      className={classNames("mx-auto max-w-7xl space-y-6 pb-16 lg:px-8 xl:px-0", {
        "max-w-full px-8 xl:px-8": fullBleed,
      })}
    >
      <TwoPassInViewRender tag="Financials" loadingSkeletonClassName="mx-auto max-w-7xl space-y-8 pb-16 xl:px-0">
        <LocalFinancialsState indicatorDataSource={IIndicatorDataSource.Cvm} currency="BRL">
          {(props) => (
            <Provider>
              <Financials
                key={format("financials/%s/tabs", tickerSymbol)}
                {...props}
                country={ISecurityCountry.Br}
                tickerSymbol={tickerSymbol}
                canChangeDataSource={true}
              />
            </Provider>
          )}
        </LocalFinancialsState>
      </TwoPassInViewRender>
    </div>
  );
}

interface IBrazilianCompanyPageHeadProps {
  issuer: NonNullable<IBrazilianCompanyIssuerTopLevelFragment>;
  tickerSymbol: string;
  security: IBrazilianCompanyIssuerTopLevelFragment["securities"][0];
}

function BrazilianCompanyPageHead({ issuer, tickerSymbol, security }: IBrazilianCompanyPageHeadProps) {
  const intl = useIntl();

  const canonicalURL =
    tickerSymbol !== security.tickerSymbol
      ? `${getBaseUrl()}/br/${security.tickerSymbol}`.toLowerCase()
      : `${getBaseUrl()}/br/${tickerSymbol}`.toLowerCase();
  const shouldUseCanonical = tickerSymbol !== security.tickerSymbol;
  const pageTitle = `${security.tickerSymbol} - ${issuer.name} | Fundamentei`;
  const description = `${tickerSymbol} - ${issuer.name}: Análise Fundamentalista, Cotação, Histórico de Balanços, Dividendos (DY), Indicadores (P/L, P/VPA) e muito mais!`;
  const gitShaShort = process.env.GIT_SHA_SHORT || "1";
  const languageCode = intl.locale === "pt-BR" ? "pt" : "en";
  const imageUrl = `${getBaseUrl()}/og/issuer/${security.tickerSymbol.toLowerCase()}?locale=${languageCode}&ns=br&v=${gitShaShort}`;
  return (
    <>
      <FundamenteiMetadata
        title={pageTitle}
        description={description}
        openGraph={{
          images: [
            {
              url: imageUrl,
              width: 1200,
              height: 630,
              alt: `${security.tickerSymbol} - ${issuer.name} no Fundamentei`,
            },
          ],
        }}
      />

      <Head>
        {shouldUseCanonical && <link rel="canonical" href={canonicalURL} key="canonical" />}

        {issuer.chiefExecutiveOfficer && (
          <script
            {...jsonLdScriptProps<Person>({
              "@context": "https://schema.org",
              "@type": "Person",
              name: issuer.chiefExecutiveOfficer.name,
              disambiguatingDescription: `CEO - ${issuer.name} (${tickerSymbol})`,
              jobTitle: "CEO",
            })}
          />
        )}

        <script
          {...jsonLdScriptProps<Corporation>({
            "@context": "https://schema.org",
            "@type": "Corporation",
            tickerSymbol: security.tickerSymbol,
            name: issuer.name,
            description: issuer.businessDescription,
            taxID: issuer.cnpj,
            foundingDate: `${issuer.foundedYear}`,
            numberOfEmployees: issuer.employeeNumbers
              ? {
                  "@type": "QuantitativeValue",
                  value: issuer.employeeNumbers?.totalOfEmployees,
                }
              : undefined,
            logo: issuer.niceLogo?.url || issuer.standardLogo?.url,
            url: issuer.investorRelationsUrl || issuer.website || canonicalURL,
            // @ts-ignore
            aggregateRating:
              // We should only have ratings for companies that have at least one vote
              issuer.overallRankingRatingSummary && issuer.overallRankingRatingSummary.totalRatings > 0
                ? {
                    bestRating: "5",
                    worstRating: "1",
                    ratingValue: String(issuer.overallRankingRatingSummary.overallRating),
                    ratingCount: String(issuer.overallRankingRatingSummary.totalRatings),
                  }
                : undefined,
          })}
        />
      </Head>
    </>
  );
}

function onToggleTickerSymbols(tickerSymbol: string, tickerSymbols: string[], set: Updater<string[]>) {
  if (tickerSymbols.length === 0) {
    set([tickerSymbol]);
  } else {
    set(tickerSymbols);
  }
}
